package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The three Uploads operations: `createPresignedUpload`, `completeUpload`, `deleteUpload`.
 *
 * [MediaObject] and [MediaPurpose] are NOT redeclared here -- they already live in `Messages.kt`
 * because a message embeds one, and a second declaration of the same contract schema is exactly
 * the drift `:protocol` exists to prevent. This file holds only the types the upload flow adds.
 *
 * The flow is three calls and one direct PUT to storage:
 *
 *   1. `POST /uploads/presign` -> [PresignUploadResponse]: an id, a URL the client may PUT to,
 *      the headers it must send, and the moment the URL stops working.
 *   2. the client PUTs the bytes straight to storage. They never pass through the API.
 *   3. `POST /uploads/complete` -> [MediaObject], carrying the public CDN URL.
 *
 * Step 2 going nowhere near the API is the whole point of presigning, and it is also why
 * [PresignUploadRequest] carries dimensions and an EXIF attestation: after step 1 the server
 * never sees the image, so anything it must know about the image has to be declared here.
 */

/**
 * `PresignUploadRequest` -- `POST /uploads/presign`.
 *
 * [purpose] deliberately carries NO Kotlin default. `MediaPurpose` has an `UNKNOWN` member like
 * every wire enum in this module, but `coerceInputValues` only coerces to a property's declared
 * default -- so without one, an unrecognised purpose fails to decode and the client gets the same
 * 400 as any other malformed body. That is the safe direction: a purpose that coerced to
 * `unknown` would pass decoding and then violate `media_purpose_valid` at the INSERT, surfacing
 * as a 500 for what is really a bad request.
 *
 * [mimeType] is a String rather than an enum for the opposite reason. The contract enumerates
 * four types, but `ERRORS.md` gives an unsupported type its own code -- `invalid_media_type`,
 * 400 -- and a Kotlin enum would swallow that distinction: the body would fail to decode and the
 * client would be told `validation_failed` instead, which says nothing about what to fix.
 * The permitted set is [UploadLimits.ALLOWED_MIME_TYPES]; the server checks it.
 *
 * [width] and [height] are optional in the contract and required in practice. `MediaObject`
 * makes both non-nullable, and after the presign the server never sees the image again -- it
 * cannot measure what it is never shown. A presign without them can therefore never be completed
 * into a conformant `MediaObject`, so the server rejects it here, before the client spends a
 * round trip uploading bytes it would have to throw away. See the note in `UploadService`.
 *
 * [exifRemoved] and [compressionApplied] are `const: true` in the contract, and optional -- so
 * they are nullable rather than defaulted. Absent means "did not say", which is permitted;
 * an explicit `false` is a client announcing it broke the rule (DECISIONS §10: the client strips
 * EXIF and GPS before upload) and is rejected. A non-null default would erase that difference,
 * and defaulting to `true` would have the server assert on the client's behalf that location
 * metadata was stripped -- a claim it has no way to check and no business making.
 */
@Serializable
data class PresignUploadRequest(
    @SerialName("purpose") val purpose: MediaPurpose,
    /**
     * Used for validation only. The server never stores it and never puts it in the object key:
     * a key is public and permanent, and `IMG_20260731_Kyiv_apartment.jpg` in one would publish
     * exactly what stripping EXIF was meant to prevent.
     */
    @SerialName("filename") val filename: String,
    @SerialName("mime_type") val mimeType: String,
    @SerialName("size_bytes") val sizeBytes: Long,
    @SerialName("width") val width: Int? = null,
    @SerialName("height") val height: Int? = null,
    /** `const: true`. Null means the client did not say; `false` is refused. */
    @SerialName("exif_removed") val exifRemoved: Boolean? = null,
    /** `const: true`. Null means the client did not say; `false` is refused. */
    @SerialName("compression_applied") val compressionApplied: Boolean? = null,
    /** Required for `chat_voice`; completion verifies it against the Ogg granule duration. */
    @SerialName("duration_ms") val durationMs: Long? = null,
    /** Normalised 0..100 samples. Kept small enough to embed in every message response. */
    @SerialName("waveform") val waveform: List<Int> = emptyList(),
    @SerialName("codec") val codec: String? = null,
)

/**
 * `PresignUploadResponse` -- the 201 body of `createPresignedUpload`.
 *
 * [uploadUrl] is a credential, not a link. Anyone holding it may write those exact bytes to that
 * exact key until [expiresAt], so it is never logged, never cached, and never shared between
 * uploads.
 *
 * [headers] is not advisory. The server signs the headers it lists, so a PUT that omits one or
 * sends a different value is rejected by storage with a signature mismatch -- which is the point:
 * `Content-Type` is signed precisely so a client cannot upload `text/html` into an image key and
 * have the CDN serve scripted content from our own domain.
 */
@Serializable
data class PresignUploadResponse(
    @SerialName("upload_id") val uploadId: Uuid,
    @SerialName("upload_url") val uploadUrl: String,
    @SerialName("expires_at") val expiresAt: Timestamp,
    @SerialName("headers") val headers: Map<String, String>,
)

/**
 * The `completeUpload` body. The contract declares it inline with no schema name, so the name
 * here is ours; the wire shape -- one required `upload_id` -- is the contract's.
 */
@Serializable
data class CompleteUploadRequest(
    @SerialName("upload_id") val uploadId: Uuid,
)

/**
 * The contract's numeric bounds, in one place both ends can read.
 *
 * These are not tuning knobs. The byte ceiling and MIME set are `PresignUploadRequest`'s own
 * schema; the pixel ceilings are DECISIONS §10. They live in `:protocol` so the client can reject
 * an oversized image before compressing and uploading it, and so the two ends cannot disagree
 * about what "too large" means -- a client with a larger idea of the limit produces uploads that
 * are refused after the bytes have already been spent.
 */
object UploadLimits {
    /** `maxLength: 255` on `filename`. */
    const val MAX_FILENAME_LENGTH = 255

    /** `maximum: 10485760` on `size_bytes` -- 10 MiB. */
    const val MAX_SIZE_BYTES = 10_485_760L

    /** DECISIONS §10: avatars are capped at 1024x1024. Applies to `group_avatar` too. */
    const val AVATAR_MAX_EDGE = 1024

    /** DECISIONS §10: a chat image is capped at 2560 px on its longest side. */
    const val CHAT_IMAGE_MAX_EDGE = 2560

    /** PICSO_PLAN: photos are capped at 2048x2048. */
    const val PICSO_PHOTO_MAX_EDGE = 2048

    const val VOICE_MIN_DURATION_MS = 1_000L
    const val VOICE_MAX_DURATION_MS = 120_000L
    const val VOICE_MAX_SIZE_BYTES = 8_388_608L
    const val VOICE_WAVEFORM_SAMPLES = 64
    const val VOICE_WAVEFORM_MAX = 100
    const val VOICE_MIME_TYPE = "audio/ogg"
    const val VOICE_CODEC = "opus"

    /** The contract's `mime_type` enum, verbatim. */
    val ALLOWED_MIME_TYPES: Set<String> =
        setOf("image/jpeg", "image/png", "image/webp", "image/heic", VOICE_MIME_TYPE)

    /**
     * The file extension for a contract MIME type, WITHOUT a leading dot. Empty for anything
     * outside [ALLOWED_MIME_TYPES].
     *
     * Here rather than in either module that uses it, because both need it and neither can see
     * the other: the client builds the `filename` it sends to `createPresignedUpload`, and the
     * server builds the object key it stores under. Two copies drifting would mean an image
     * uploaded as `.jpg` and served under a key claiming something else -- a mismatch nothing
     * would fail on, and that only a CDN serving the wrong `Content-Type` would ever reveal.
     *
     * Callers adapt the shape they need (a dot prefix, a fallback for the empty case) rather than
     * this guessing which they wanted.
     */
    fun extensionFor(mimeType: String): String = when (mimeType.lowercase()) {
        "image/jpeg" -> "jpg"
        "image/png" -> "png"
        "image/webp" -> "webp"
        "image/heic" -> "heic"
        VOICE_MIME_TYPE -> "ogg"
        else -> ""
    }

    /** The longest permitted edge for [purpose], or null when the purpose is not an image. */
    fun maxEdgeFor(purpose: MediaPurpose): Int? = when (purpose) {
        MediaPurpose.AVATAR, MediaPurpose.GROUP_AVATAR -> AVATAR_MAX_EDGE
        MediaPurpose.CHAT_IMAGE -> CHAT_IMAGE_MAX_EDGE
        MediaPurpose.PICSO_PHOTO -> PICSO_PHOTO_MAX_EDGE
        MediaPurpose.CHAT_VOICE -> null
        MediaPurpose.UNKNOWN -> null
    }
}
